import {useCallback, useEffect, useMemo, useState} from 'react';

import {STRUCTURED_TABLE_INVOKE_MAPPINGS} from 'constants/EventConstants';
import DocumentActions from 'notebook/documents/DocumentActions';
import * as DocumentEvents from 'notebook/documents/DocumentEvents';
import DocumentStore from 'notebook/documents/DocumentStore';
import DocumentUtil from 'notebook/documents/DocumentUtil';
import useDocumentSaveState from 'notebook/documents/useDocumentSaveState';
import {EntryHistoryState} from 'notebook/ELNConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import DocumentUpdatesManager from 'notebook/realtime/DocumentUpdatesManager';
// @ts-expect-error
import HistoryManager from 'notebook/store/HistoryManager';
import {StructuredTableMapping} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingConstants';
import {Note} from 'notebook/util/NoteUtilConstants';
import DocumentEditorLockUtil from 'realtime/util/DocumentEditorLockUtil';

interface UseDocument {
  getIdFromCid: (cid: string) => string | undefined;
  historyManager: HistoryManager;
  isLatest: boolean;
  note?: Note;
  parentType: string;
  registerObjects: (cidIdMap: unknown) => void;
  updateNote: (
    note: Note,
    options?: {performTableEvaluation?: boolean},
    structuredTableMappings?: Array<StructuredTableMapping>
  ) => void;
  updateTitle: (title: string) => void;
  restoreStateAndSelection: (state: EntryHistoryState, selectionData: SelectionRestoreData) => void;
}

/** The following event types when performed on the DocumentStore will cause the onChange callback
 * to be triggered.
 */
const EVENTS_THAT_PROMPT_DOCUMENT_CHANGE = [
  DocumentEvents.DOCUMENT_UPDATE,
  DocumentEvents.DOCUMENT_SERVER_SIDE_UPDATE,
  STRUCTURED_TABLE_INVOKE_MAPPINGS,
] satisfies Array<string>;

export default function useDocument(
  documentId: string,
  disableAutoSaver?: boolean,
  onChange?: (wasServerUpdate?: boolean) => void,
  customSaveHandler?: () => Promise<boolean>,
  manageRealtimeUpdates: boolean = false
): UseDocument {
  const [historyManager, setHistoryManager] = useState(new HistoryManager());
  const documentUpdatesManager = useMemo(
    () => (manageRealtimeUpdates ? new DocumentUpdatesManager() : null),
    [manageRealtimeUpdates]
  );

  const parentType = 'Note';

  const {isLatest, latestVersionId} = useDocumentSaveState(documentId);

  const registerObjects: (cidIdMap: unknown) => void = useCallback(
    (cidIdMap: CidIdMap) => {
      DocumentActions.registerObjects(documentId, cidIdMap);
    },
    [documentId]
  );

  const getIdFromCid: (cid: string) => string | undefined = useCallback(
    (cid: string) => DocumentStore.getIdFromCid(documentId, cid),
    [documentId]
  );

  const updateNote = useCallback(
    (
      note: Note,
      options?: {performTableEvaluation?: boolean},
      structuredTableMappings?: Array<StructuredTableMapping>
    ): void => {
      DocumentActions.updateNote(documentId, DocumentUtil.toEntryNote(note), {structuredTableMappings});
    },
    [documentId]
  );

  const updateTitle: (title: string) => void = useCallback(
    (name: string) => {
      DocumentActions.setName(documentId, name);
    },
    [documentId]
  );

  const restoreStateAndSelection = useCallback(
    (state: EntryHistoryState, selectionData: SelectionRestoreData): void => {
      DocumentActions.revert(
        documentId,
        {
          name: {$set: state.title},
          ...DocumentUtil.entryVersionUpdate({$merge: {note: state.note}}),
        },
        selectionData
      );
    },
    [documentId]
  );

  /**
   * Open the document, fetching it if needed, and set a custom save handler if provided
   */
  useEffect(() => {
    const openDocument = async (): Promise<void> => {
      const document = DocumentStore.getDocument(documentId);
      historyManager.reset();
      if (!document || document.isStale) {
        await DocumentActions.open(documentId);
        if (customSaveHandler) {
          DocumentStore.setCustomSaveHandler(documentId, customSaveHandler);
        }
        if (disableAutoSaver) {
          DocumentStore.disableAutoSaver(documentId);
        }
      }
    };
    openDocument();
  }, [documentId, disableAutoSaver, customSaveHandler, historyManager]);

  /**
   * Add a listener for document updates to trigger the onChange callback
   */
  useEffect(() => {
    const callback = onChange
      ? (context: {id?: string; actionType: string}): void => {
          const {id, actionType} = context;
          if (
            documentId === id &&
            DocumentStore.inStore(documentId) &&
            EVENTS_THAT_PROMPT_DOCUMENT_CHANGE.includes(actionType)
          ) {
            onChange(actionType === DocumentEvents.DOCUMENT_SERVER_SIDE_UPDATE);
          }
        }
      : null;
    if (callback) {
      DocumentStore.addChangeListener(callback);
    }
    return () => {
      if (callback) {
        DocumentStore.removeChangeListener(callback);
      }
    };
  }, [documentId, onChange]);

  /**
   * Add a listener for server side updates to trigger a reload of the history manager
   */
  useEffect(() => {
    const onServerSideUpdate = (context: {id?: string; actionType: string}): void => {
      const {id, actionType} = context;
      if (
        documentId === id &&
        DocumentStore.inStore(documentId) &&
        actionType === DocumentEvents.DOCUMENT_SERVER_SIDE_UPDATE
      ) {
        setHistoryManager(new HistoryManager());
      }
    };
    DocumentStore.addChangeListener(onServerSideUpdate);
    return () => {
      DocumentStore.removeChangeListener(onServerSideUpdate);
    };
  }, [documentId]);

  useEffect(() => {
    documentUpdatesManager?.changeActiveDocument(documentId);
    return () => {
      documentUpdatesManager?.markInactive();
    };
  }, [documentId, documentUpdatesManager]);

  /**
   * Load the latest version if it is different from the current version
   */
  useEffect(() => {
    if (
      latestVersionId !== undefined &&
      (!DocumentStore.getDocument(documentId) ||
        (!DocumentStore.hasUnsavedChanges(documentId) &&
          DocumentStore.getVersionState(documentId).openVersionId !== latestVersionId))
    ) {
      DocumentActions.loadVersion(documentId, latestVersionId);
      setHistoryManager(new HistoryManager());
    }

    // Release lock on unmount
    return () => {
      DocumentEditorLockUtil.releaseLock(documentId);
    };
    // We don't want to reload every time the latest version updates, only when we change to a different doc
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  return {
    getIdFromCid,
    historyManager,
    isLatest,
    parentType,
    registerObjects,
    updateNote,
    updateTitle,
    restoreStateAndSelection,
  };
}
